import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import "./WorkflowMasterPage.css";

import ConditionInputPage from "./components/conditionInputpage";

import {
  workflowMasterAPI,
} from "../../api/client";


/*
|--------------------------------------------------------------------------
| EMPTY FORM
|--------------------------------------------------------------------------
*/

const emptyForm = {
  business_unit: "",
  division: "",

  stage2_name: "",
  stage2_email: "",

  stage3_name: "",
  stage3_email: "",

  active: true,
};


/*
|--------------------------------------------------------------------------
| MAIN PAGE
|--------------------------------------------------------------------------
*/

export default function WorkflowMasterPage() {

  /*
  |--------------------------------------------------------------------------
  | ACTIVE TAB
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  | Use ONLY these two values:
  |
  | workflow-master
  | conditional-offer
  |
  */

  const [
    activeTab,
    setActiveTab,
  ] = useState("workflow-master");


  /*
  |--------------------------------------------------------------------------
  | WORKFLOW DATA
  |--------------------------------------------------------------------------
  */

  const [
    workflows,
    setWorkflows,
  ] = useState([]);


  /*
  |--------------------------------------------------------------------------
  | MASTER OPTIONS
  |--------------------------------------------------------------------------
  */

  const [
    masterOptions,
    setMasterOptions,
  ] = useState({
    businessUnits: [],
    divisions: [],
  });

//DLETE FUNCATION 

/*
|--------------------------------------------------------------------------
| DELETE WORKFLOW
|--------------------------------------------------------------------------
*/

const handleDelete = async (workflow) => {

  if (!workflow?.id) {
    toast.error(
      "Invalid workflow configuration"
    );

    return;
  }


  const confirmed =
    window.confirm(
      `Are you sure you want to delete this workflow configuration?\n\n` +
      `Business Unit: ${
        workflow.business_unit || "-"
      }\n` +
      `Division: ${
        workflow.division || "-"
      }\n\n` +
      `This action cannot be undone.`
    );


  if (!confirmed) {
    return;
  }


  try {

    setLoading(true);


    await workflowMasterAPI.delete(
      workflow.id
    );


    toast.success(
      "Workflow configuration deleted successfully"
    );


    /*
     * Reload from database.
     */

    await loadWorkflowMaster();

  } catch (error) {

    console.error(
      "Failed to delete workflow:",
      error
    );


    toast.error(
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to delete workflow configuration"
    );

  } finally {

    setLoading(false);

  }

};

  /*
  |--------------------------------------------------------------------------
  | FORM
  |--------------------------------------------------------------------------
  */

  const [
    form,
    setForm,
  ] = useState({
    ...emptyForm,
  });


  /*
  |--------------------------------------------------------------------------
  | EDITING
  |--------------------------------------------------------------------------
  */

  const [
    editingWorkflow,
    setEditingWorkflow,
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | MODAL
  |--------------------------------------------------------------------------
  */

  const [
    showModal,
    setShowModal,
  ] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    loadingOptions,
    setLoadingOptions,
  ] = useState(false);


  const [
    saving,
    setSaving,
  ] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    loadInitialData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  /*
  |--------------------------------------------------------------------------
  | LOAD INITIAL DATA
  |--------------------------------------------------------------------------
  */

  const loadInitialData = async () => {

    try {

      setLoading(true);

      await Promise.all([
        loadWorkflowMaster(),
        loadMasterOptions(),
      ]);

    } catch (error) {

      console.error(
        "Workflow master initial load failed:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load workflow master"
      );

    } finally {

      setLoading(false);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | LOAD WORKFLOW MASTER
  |--------------------------------------------------------------------------
  */

  const loadWorkflowMaster = async () => {

    try {

      const response =
        await workflowMasterAPI.getAll();

      const rows =
        Array.isArray(
          response?.data?.data
        )
          ? response.data.data
          : [];

      setWorkflows(rows);

      return rows;

    } catch (error) {

      console.error(
        "Failed to load workflow master:",
        error
      );

      throw error;

    }

  };


  /*
  |--------------------------------------------------------------------------
  | LOAD MASTER OPTIONS
  |--------------------------------------------------------------------------
  */

  const loadMasterOptions = async () => {

    try {

      setLoadingOptions(true);

      const response =
        await workflowMasterAPI.getOptions();

      const data =
        response?.data || {};

      setMasterOptions({

        businessUnits:
          Array.isArray(
            data.businessUnits
          )
            ? data.businessUnits
            : [],

        divisions:
          Array.isArray(
            data.divisions
          )
            ? data.divisions
            : [],

      });

    } catch (error) {

      console.error(
        "Failed to load workflow master options:",
        error
      );

      toast.error(
        error?.response?.data?.error ||
        "Unable to load employee master data"
      );

      throw error;

    } finally {

      setLoadingOptions(false);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | BUSINESS UNIT OPTIONS
  |--------------------------------------------------------------------------
  */

  const businessUnitOptions =
    useMemo(() => {

      const values =
        masterOptions.businessUnits
          .map((item) => {

            if (
              typeof item ===
              "string"
            ) {
              return item;
            }

            return (
              item?.business_unit ||
              item?.name ||
              item?.value ||
              ""
            );

          })
          .map((value) =>
            String(value).trim()
          )
          .filter(Boolean);


      return [
        ...new Set(values),
      ].sort();

    }, [
      masterOptions.businessUnits,
    ]);


  /*
  |--------------------------------------------------------------------------
  | DIVISION OPTIONS
  |--------------------------------------------------------------------------
  */

  const divisionOptions =
    useMemo(() => {

      const selectedBU =
        String(
          form.business_unit || ""
        )
          .trim()
          .toLowerCase();


      return masterOptions.divisions

        .map((item) => {

          if (
            typeof item ===
            "string"
          ) {

            return {
              division: item,
              business_unit: "",
            };

          }


          return {
            division:
              item?.division ||
              item?.name ||
              item?.value ||
              "",

            business_unit:
              item?.business_unit ||
              "",
          };

        })

        .filter((item) => {

          if (!item.division) {
            return false;
          }


          /*
           * If backend sends BU-specific
           * division records, filter them.
           */

          if (
            item.business_unit
          ) {

            return (
              String(
                item.business_unit
              )
                .trim()
                .toLowerCase() ===
              selectedBU
            );

          }


          return true;

        })

        .map(
          (item) =>
            item.division
        )

        .filter(Boolean);

    }, [
      masterOptions.divisions,
      form.business_unit,
    ]);


  /*
  |--------------------------------------------------------------------------
  | HANDLE FORM CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
      type,
      checked,
    } = event.target;


    /*
    ------------------------------------------------------
    BUSINESS UNIT
    ------------------------------------------------------
    */

    if (
      name ===
      "business_unit"
    ) {

      const newBU =
        value;


      setForm(
        (previous) => ({
          ...previous,

          business_unit:
            newBU,

          division:
            "",
        })
      );


      /*
       * Load divisions and BU Heads
       * for selected BU.
       */

      (async () => {

        try {

          setLoadingOptions(true);


          const response =
            await workflowMasterAPI
              .getOptions({
                business_unit:
                  newBU,
              });


          const data =
            response?.data || {};


          setMasterOptions(
            (previous) => ({
              ...previous,

              businessUnits:
                data.businessUnits ||
                previous.businessUnits,

              divisions:
                data.divisions ||
                previous.divisions,

            })
          );

        } catch (error) {

          console.error(
            "Failed to fetch workflow options for BU:",
            error
          );

          toast.error(
            error?.response?.data?.error ||
            "Failed to load divisions"
          );

        } finally {

          setLoadingOptions(false);

        }

      })();


      return;
    }


    /*
    ------------------------------------------------------
    DIVISION
    ------------------------------------------------------
    */

    if (
      name ===
      "division"
    ) {

      const newDivision =
        value;

      const selectedBU =
        form.business_unit || "";


      setForm(
        (previous) => ({
          ...previous,

          division:
            newDivision,
        })
      );


      /*
       * Load BU Heads for selected
       * Business Unit + Division.
       */

      (async () => {

        try {

          setLoadingOptions(true);


          const response =
            await workflowMasterAPI
              .getOptions({
                business_unit:
                  selectedBU,

                division:
                  newDivision,
              });


          const data =
            response?.data || {};


          setMasterOptions(
            (previous) => ({
              ...previous,

              businessUnits:
                data.businessUnits ||
                previous.businessUnits,

              divisions:
                data.divisions ||
                previous.divisions,

            })
          );

        } catch (error) {

          console.error(
            "Failed to fetch workflow options for Division:",
            error
          );

          toast.error(
            error?.response?.data?.error ||
            "Failed to load BU Heads"
          );

        } finally {

          setLoadingOptions(false);

        }

      })();


      return;
    }


    /*
    ------------------------------------------------------
    NORMAL FIELD
    ------------------------------------------------------
    */

    setForm(
      (previous) => ({
        ...previous,

        [name]:
          type ===
          "checkbox"
            ? checked
            : value,
      })
    );

  };


  /*
  |--------------------------------------------------------------------------
  | OPEN ADD
  |--------------------------------------------------------------------------
  */

  const handleAdd = () => {

    /*
     * This button belongs only to
     * Workflow Master.
     */

    setActiveTab(
      "workflow-master"
    );

    setEditingWorkflow(null);

    setForm({
      ...emptyForm,
    });

    setShowModal(true);

  };


  /*
  |--------------------------------------------------------------------------
  | OPEN EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (
    workflow
  ) => {

    /*
     * Make sure Workflow tab
     * is active.
     */

    setActiveTab(
      "workflow-master"
    );


    setEditingWorkflow(
      workflow
    );


    /*
     * Stage 2 and Stage 3 are free-text inputs;
     * prefill directly from the workflow row.
     */

    setForm({

      business_unit:
        workflow?.business_unit ||
        "",

      division:
        workflow?.division ||
        "",


      stage2_name:
        workflow?.stage2_name ||
        "",

      stage2_email:
        workflow?.stage2_email ||
        "",


      stage3_name:
        workflow?.stage3_name ||
        "",

      stage3_email:
        workflow?.stage3_email ||
        "",


      active:
        Boolean(
          workflow?.active
        ),

    });


    setShowModal(true);

  };


  /*
  |--------------------------------------------------------------------------
  | CLOSE MODAL
  |--------------------------------------------------------------------------
  */

  const handleClose = () => {

    if (saving) {
      return;
    }


    setShowModal(false);

    setEditingWorkflow(
      null
    );

    setForm({
      ...emptyForm,
    });

  };


  /*
  |--------------------------------------------------------------------------
  | VALIDATE FORM
  |--------------------------------------------------------------------------
  */

  const validateForm = () => {

    if (
      !form.business_unit
    ) {

      toast.error(
        "Please select Business Unit"
      );

      return false;

    }


    if (
      !form.division
    ) {

      toast.error(
        "Please select Division"
      );

      return false;

    }


    if (
      !form.stage2_name
    ) {

      toast.error(
        "Please enter BU Head name"
      );

      return false;

    }


    if (
      !form.stage2_email
    ) {

      toast.error(
        "Please select BU Head"
      );

      return false;

    }


    if (
      !form.stage3_email
    ) {

      toast.error(
        "Please enter HR email"
      );

      return false;

    }


    return true;

  };


  /*
  |--------------------------------------------------------------------------
  | SAVE WORKFLOW
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (event) => {

      event.preventDefault();


      if (
        !validateForm()
      ) {
        return;
      }


      try {

        setSaving(true);


        const payload = {

          business_unit:
            form.business_unit,

          division:
            form.division,

          stage2_name:
            form.stage2_name,

          stage2_email:
            form.stage2_email,

          stage3_name:
            form.stage3_name,

          stage3_email:
            form.stage3_email,

          active:
            form.active
              ? 1
              : 0,

        };


        /*
        --------------------------------------------------
        CREATE
        --------------------------------------------------
        */

        if (
          !editingWorkflow
        ) {

          await workflowMasterAPI.create(
            payload
          );


          toast.success(
            "Workflow configuration created successfully"
          );

        }


        /*
        --------------------------------------------------
        UPDATE
        --------------------------------------------------
        */

        else {

          await workflowMasterAPI.update(
            editingWorkflow.id,
            payload
          );


          toast.success(
            "Workflow configuration updated successfully"
          );

        }


        /*
        --------------------------------------------------
        RELOAD
        --------------------------------------------------
        */

        await loadWorkflowMaster();


        /*
        --------------------------------------------------
        CLOSE
        --------------------------------------------------
        */

        handleClose();

      } catch (error) {

        console.error(
          "Failed to save workflow:",
          error
        );


        toast.error(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save workflow configuration"
        );

      } finally {

        setSaving(false);

      }

    };


  /*
  |--------------------------------------------------------------------------
  | TOGGLE ACTIVE
  |--------------------------------------------------------------------------
  */

  const handleToggleActive =
    async (workflow) => {

      try {

        const newStatus =
          workflow.active
            ? 0
            : 1;


        await workflowMasterAPI.update(
          workflow.id,
          {

            business_unit:
              workflow.business_unit,

            division:
              workflow.division,

            stage2_name:
              workflow.stage2_name,

            stage2_email:
              workflow.stage2_email,

            stage3_name:
              workflow.stage3_name,

            stage3_email:
              workflow.stage3_email,

            active:
              newStatus,

          }
        );


        toast.success(
          newStatus
            ? "Workflow activated"
            : "Workflow deactivated"
        );


        await loadWorkflowMaster();

      } catch (error) {

        console.error(
          "Failed to change workflow status:",
          error
        );


        toast.error(
          error?.response?.data?.error ||
          error?.message ||
          "Failed to update workflow status"
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | REFRESH
  |--------------------------------------------------------------------------
  */

  const handleRefresh =
    async () => {

      try {

        setLoading(true);


        await Promise.all([
          loadWorkflowMaster(),
          loadMasterOptions(),
        ]);


        toast.success(
          "Workflow data refreshed"
        );

      } catch (error) {

        console.error(
          "Refresh failed:",
          error
        );


        toast.error(
          error?.response?.data?.error ||
          "Failed to refresh workflow data"
        );

      } finally {

        setLoading(false);

      }

    };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div className="workflow-master-page">


      {/* =====================================================
          PAGE HEADER
      ====================================================== */}

      <div className="workflow-master-header">

        <div>

          <div className="workflow-breadcrumb">
            Super Admin / Master Settings / Workflow
          </div>


          <h1>
            Offer Workflow Management
          </h1>


          <p>
            Configure the BU Head and HR approvers
            used in the offer approval workflow.
          </p>

        </div>


        {/* ---------------------------------------------------
            ADD WORKFLOW ONLY ON WORKFLOW MASTER TAB
        --------------------------------------------------- */}

        {activeTab ===
          "workflow-master" && (

          <button
            type="button"
            className="workflow-add-button"
            onClick={handleAdd}
          >

            <span>
              +
            </span>

            Add Workflow

          </button>

        )}

      </div>


      {/* =====================================================
          TABS
      ====================================================== */}

      <div className="workflow-tabs">

        <button
          type="button"
          className={
            activeTab ===
            "workflow-master"
              ? "workflow-tab active"
              : "workflow-tab"
          }
          onClick={() => {

            console.log(
              "Workflow Master tab clicked"
            );

            setActiveTab(
              "workflow-master"
            );

          }}
        >
          Workflow Master
        </button>


        <button
          type="button"
          className={
            activeTab ===
            "conditional-offer"
              ? "workflow-tab active"
              : "workflow-tab"
          }
          onClick={() => {

            console.log(
              "Conditional Offers tab clicked"
            );

            setActiveTab(
              "conditional-offer"
            );

          }}
        >
          Conditional Offers
        </button>

      </div>


      {/* =====================================================
          CONDITIONAL OFFERS TAB
      ====================================================== */}

      {activeTab ===
        "conditional-offer" && (

        <div className="workflow-conditional-tab">

          <ConditionInputPage />

        </div>

      )}


      {/* =====================================================
          WORKFLOW MASTER TAB
      ====================================================== */}

      {activeTab ===
        "workflow-master" && (

        <>

          {/* =================================================
              INFORMATION
          ================================================== */}

          <div className="workflow-info-card">

            <div className="workflow-info-icon">
              i
            </div>


            <div>

              <strong>
                Approval workflow
              </strong>


              <p>
                Stage 1 is automatically taken
                from the Hiring Manager assigned
                to the job. Stage 2 is selected
                from the BU Head master data.
                Stage 3 is selected from the
                existing HR employee data.
              </p>

            </div>

          </div>


          {/* =================================================
              TABLE
          ================================================== */}

          <div className="workflow-table-card">


            <div className="workflow-table-header">

              <div>

                <h2>
                  Workflow Configurations
                </h2>


                <span>

                  {workflows.length}
                  {" "}
                  configuration
                  {workflows.length !==
                    1
                    ? "s"
                    : ""}

                </span>

              </div>


              <button
                type="button"
                className="workflow-refresh-button"
                onClick={
                  handleRefresh
                }
                disabled={
                  loading
                }
              >

                {loading
                  ? "Refreshing..."
                  : "Refresh"}

              </button>

            </div>


            {/* ------------------------------------------------
                LOADING
            ------------------------------------------------- */}

            {loading && (

              <div className="workflow-loading">

                Loading workflow configurations...

              </div>

            )}


            {/* ------------------------------------------------
                EMPTY
            ------------------------------------------------- */}

            {!loading &&
              workflows.length ===
                0 && (

              <div className="workflow-empty">

                <div className="workflow-empty-icon">
                  +
                </div>


                <h3>
                  No workflow configuration
                </h3>


                <p>
                  Create a configuration
                  for a Business Unit
                  and Division.
                </p>


                <button
                  type="button"
                  className="workflow-add-button"
                  onClick={
                    handleAdd
                  }
                >

                  <span>
                    +
                  </span>

                  Add Workflow

                </button>

              </div>

            )}


            {/* ------------------------------------------------
                TABLE
            ------------------------------------------------- */}

            {!loading &&
              workflows.length >
                0 && (

              <div className="workflow-table-wrapper">

                <table className="workflow-table">

                  <thead>

                    <tr>

                      <th scope="col">
                        Business Unit
                      </th>

                      <th scope="col">
                        Division
                      </th>

                      <th scope="col">
                        Stage 2 — BU Head
                      </th>

                      <th scope="col">
                        Stage 3 — HR
                      </th>

                      <th scope="col">
                        Status
                      </th>

                      <th scope="col">
                        Action
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {workflows.map(
                      (workflow) => (

                      <tr
                        key={
                          workflow.id
                        }
                      >

                        {/* BUSINESS UNIT */}

                        <td>

                          <div className="workflow-primary-text">

                            {
                              workflow.business_unit ||
                              "-"
                            }

                          </div>

                        </td>


                        {/* DIVISION */}

                        <td>

                          <div className="workflow-primary-text">

                            {
                              workflow.division ||
                              "-"
                            }

                          </div>

                        </td>


                        {/* STAGE 2 */}

                        <td>

                          <div className="workflow-stage">

                            <div className="workflow-stage-number stage-two">

                              2

                            </div>


                            <div>

                              <div className="workflow-primary-text">

                                {
                                  workflow.stage2_name ||
                                  "-"
                                }

                              </div>


                              <div className="workflow-email">

                                {
                                  workflow.stage2_email ||
                                  "-"
                                }

                              </div>

                            </div>

                          </div>

                        </td>


                        {/* STAGE 3 */}

                        <td>

                          <div className="workflow-stage">

                            <div className="workflow-stage-number stage-three">

                              3

                            </div>


                            <div>

                              <div className="workflow-primary-text">

                                {
                                  workflow.stage3_name ||
                                  "-"
                                }

                              </div>


                              <div className="workflow-email">

                                {
                                  workflow.stage3_email ||
                                  "-"
                                }

                              </div>

                            </div>

                          </div>

                        </td>


                        {/* STATUS */}

                        <td>

                          {workflow.active ? (

                            <span className="workflow-status active">

                              <span className="status-dot" />

                              Active

                            </span>

                          ) : (

                            <span className="workflow-status inactive">

                              <span className="status-dot" />

                              Inactive

                            </span>

                          )}

                        </td>


                        {/* ACTION */}

                        <td>

                         <div className="workflow-actions">

  <button
    type="button"
    className="workflow-edit-button"
    onClick={() =>
      handleEdit(workflow)
    }
    disabled={loading}
  >
    Edit
  </button>


  <button
    type="button"
    className={
      workflow.active
        ? "workflow-disable-button"
        : "workflow-enable-button"
    }
    onClick={() =>
      handleToggleActive(workflow)
    }
    disabled={loading}
  >

    {workflow.active
      ? "Disable"
      : "Enable"}

  </button>


  <button
    type="button"
    className="workflow-delete-button"
    onClick={() =>
      handleDelete(workflow)
    }
    disabled={loading}
  >
    Delete
  </button>

</div>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

          </div>


          {/* =================================================
              WORKFLOW MODAL
          ================================================== */}

          {showModal && (

            <div
              className="workflow-modal-overlay"
              onMouseDown={(
                event
              ) => {

                if (
                  event.target ===
                  event.currentTarget
                ) {

                  handleClose();

                }

              }}
            >

              <div className="workflow-modal">


                {/* MODAL HEADER */}

                <div className="workflow-modal-header">

                  <div>

                    <h2>

                      {editingWorkflow
                        ? "Edit Workflow Configuration"
                        : "Add Workflow Configuration"}

                    </h2>


                    <p>
                      Select the workflow approvers.
                      Names and emails are automatically
                      populated from master data.
                    </p>

                  </div>


                  <button
                    type="button"
                    className="workflow-modal-close"
                    onClick={
                      handleClose
                    }
                    disabled={
                      saving
                    }
                  >

                    ×

                  </button>

                </div>


                {/* FORM */}

                <form
                  onSubmit={
                    handleSubmit
                  }
                  className="workflow-form"
                >


                  {/* ==========================================
                      WORKFLOW SCOPE
                  =========================================== */}

                  <div className="workflow-form-section">

                    <div className="workflow-section-title">

                      Workflow Scope

                    </div>


                    <div className="workflow-form-grid">


                      {/* BUSINESS UNIT */}

                      <div className="workflow-field">

                        <label>

                          Business Unit

                          <span>
                            *
                          </span>

                        </label>


                        <select
                          name="business_unit"
                          value={
                            form.business_unit
                          }
                          onChange={
                            handleChange
                          }
                          disabled={
                            loadingOptions ||
                            saving
                          }
                        >

                          <option value="">

                            {loadingOptions
                              ? "Loading business units..."
                              : "Select Business Unit"}

                          </option>


                          {businessUnitOptions.map(
                            (
                              businessUnit
                            ) => (

                              <option
                                key={
                                  businessUnit
                                }
                                value={
                                  businessUnit
                                }
                              >

                                {
                                  businessUnit
                                }

                              </option>

                            )
                          )}

                        </select>

                      </div>


                      {/* DIVISION */}

                      <div className="workflow-field">

                        <label>

                          Division

                          <span>
                            *
                          </span>

                        </label>


                        <select
                          name="division"
                          value={
                            form.division
                          }
                          onChange={
                            handleChange
                          }
                          disabled={
                            !form.business_unit ||
                            loadingOptions ||
                            saving
                          }
                        >

                          <option value="">

                            {!form.business_unit
                              ? "Select Business Unit first"
                              : loadingOptions
                              ? "Loading divisions..."
                              : "Select Division"}

                          </option>


                          {divisionOptions.map(
                            (
                              division
                            ) => (

                              <option
                                key={
                                  division
                                }
                                value={
                                  division
                                }
                              >

                                {
                                  division
                                }

                              </option>

                            )
                          )}

                        </select>

                      </div>

                    </div>

                  </div>


                  {/* ==========================================
                      STAGE 1
                  =========================================== */}

                  <div className="workflow-form-section locked-section">

                    <div className="workflow-section-title">

                      <span className="stage-badge stage-one">
                        1
                      </span>

                      Stage 1 — Hiring Manager

                      <span className="locked-label">
                        Automatically from Job
                      </span>

                    </div>


                    <div className="workflow-locked-message">

                      <span className="lock-icon">
                        
                      </span>


                      <div>

                        <strong>
                          Stage 1 cannot be edited here
                        </strong>


                        <p>
                          The Hiring Manager is
                          automatically taken from
                          the selected job when the
                          offer is created.
                        </p>

                      </div>

                    </div>

                  </div>


                  {/* ==========================================
                      STAGE 2
                  =========================================== */}

                  <div className="workflow-form-section">

                    <div className="workflow-section-title">

                      <span className="stage-badge stage-two">
                        2
                      </span>

                      Stage 2 — BU Head

                    </div>


                    <div className="workflow-form-grid">

                      {/* STAGE 2 NAME */}

                      <div className="workflow-field">

                        <label>

                          BU Head Name

                          <span>
                            *
                          </span>

                        </label>


                        <input
                          type="text"
                          name="stage2_name"
                          value={
                            form.stage2_name
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Enter BU Head name..."
                          disabled={
                            saving
                          }
                        />

                      </div>


                      {/* STAGE 2 EMAIL */}

                      <div className="workflow-field">

                        <label>

                          BU Head Email

                          <span>
                            *
                          </span>

                        </label>


                        <input
                          type="email"
                          name="stage2_email"
                          value={
                            form.stage2_email
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Enter BU Head email..."
                          disabled={
                            saving
                          }
                        />

                      </div>

                    </div>


                    <div className="workflow-field-note">

                      Stage 2 is entered manually.
                      The value is stored in the
                      database as configured,
                      without fetching from
                      master data.

                    </div>

                  </div>


                  {/* ==========================================
                      STAGE 3
                  =========================================== */}

                  <div className="workflow-form-section">

                    <div className="workflow-section-title">

                      <span className="stage-badge stage-three">
                        3
                      </span>

                      Stage 3 — HR

                    </div>


                    <div className="workflow-form-grid">

                      {/* STAGE 3 NAME */}

                      <div className="workflow-field">

                        <label>

                          HR Name

                          <span>
                            *
                          </span>

                        </label>


                        <input
                          type="text"
                          name="stage3_name"
                          value={
                            form.stage3_name
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Enter HR name..."
                          disabled={
                            saving
                          }
                        />

                      </div>


                      {/* STAGE 3 EMAIL */}

                      <div className="workflow-field">

                        <label>

                          HR Email

                          <span>
                            *
                          </span>

                        </label>


                        <input
                          type="email"
                          name="stage3_email"
                          value={
                            form.stage3_email
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Enter HR email..."
                          disabled={
                            saving
                          }
                        />

                      </div>

                    </div>


                    <div className="workflow-field-note">

                      Stage 3 is entered manually.
                      The value is stored in the
                      database as configured,
                      without fetching from
                      master data.

                    </div>

                  </div>


                  {/* ==========================================
                      STATUS
                  =========================================== */}

                  <div className="workflow-form-section">

                    <div className="workflow-section-title">

                      Configuration Status

                    </div>


                    <label className="workflow-toggle">

                      <input
                        type="checkbox"
                        name="active"
                        checked={
                          form.active
                        }
                        onChange={
                          handleChange
                        }
                        disabled={
                          saving
                        }
                      />


                      <span className="workflow-toggle-slider" />


                      <span className="workflow-toggle-text">

                        <strong>
                          Active
                        </strong>


                        <small>
                          This configuration can
                          be used by the offer
                          approval workflow.
                        </small>

                      </span>

                    </label>

                  </div>


                  {/* ==========================================
                      FOOTER
                  =========================================== */}

                  <div className="workflow-modal-footer">

                    <button
                      type="button"
                      className="workflow-cancel-button"
                      onClick={
                        handleClose
                      }
                      disabled={
                        saving
                      }
                    >

                      Cancel

                    </button>


                    <button
                      type="submit"
                      className="workflow-save-button"
                      disabled={
                        saving
                      }
                    >

                      {saving
                        ? "Saving..."
                        : editingWorkflow
                        ? "Save Changes"
                        : "Create Workflow"}

                    </button>

                  </div>

                </form>

              </div>

            </div>

          )}

        </>

      )}

    </div>

  );

}